import { useAuth } from '@client/hooks/useAuth';
import { useStoreMutation } from '@client/queries/useStoreMutation';
import { useStoreQuery } from '@client/queries/useStoreQuery';
import { subscriptionKeys } from '@client/queries/useSubscriptionQueries';
import { tenantKeys } from '@client/queries/useTenantQueries';
import {
  createFeature,
  deactivateFeature,
  deleteFeature,
  getActiveFeatures,
  getAllFeatures,
  getFeature,
  getUserFeatures,
  toggleFeatureStatus,
  togglePublic,
  updateFeature,
} from '@client/services/feature.service';
import { getSubscriptionsByFeature } from '@client/services/subscription.service';
import { getTenantsBySubscription } from '@client/services/tenant.service';
import { useQueryClientStore } from '@client/store/queryClient.store';
import {
  EErrorType,
  type EFeatureType,
  type IFeature,
  type ISubscription,
} from '@common/types';

import type { TPaginateProps } from '@client/types/antd.type';
import type { TablePaginationConfig } from 'antd/es/table/interface';

// Query keys
export const featureKeys = {
  all: ['features'] as const,
  lists: () => [...featureKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) =>
    [...featureKeys.lists(), filters] as const,
  details: () => [...featureKeys.all, 'detail'] as const,
  detail: (id: string) => [...featureKeys.details(), id] as const,
};

export const useFeatureQueries = () => {
  const { user } = useAuth();

  // Get all features
  const useAllFeatures = (params?: TPaginateProps & TablePaginationConfig) =>
    useStoreQuery({
      queryKey: featureKeys.list({ type: 'all', ...params }),
      queryFn: () => getAllFeatures(params),
      select: (result) => result || [],
    });

  // Get user features
  const useUserFeatures = (params?: TPaginateProps & TablePaginationConfig) =>
    useStoreQuery({
      queryKey: featureKeys.list({ type: 'user', ...params }),
      queryFn: () => getUserFeatures(params),
      enabled: !!user,
    });

  // Get active features by type
  const useActiveFeatures = (
    type: keyof typeof EFeatureType,
    params?: TPaginateProps & TablePaginationConfig,
  ) =>
    useStoreQuery({
      queryKey: featureKeys.list({ type, active: true, ...params }),
      queryFn: () => getActiveFeatures(type, params),
      enabled: !!user && !!type,
    });

  // Get feature by id
  const useUserFeature = (id: string) =>
    useStoreQuery({
      queryKey: featureKeys.detail(id),
      queryFn: () => getFeature(id),
      enabled: !!id,
    });

  // Create feature mutation
  const useCreateFeature = () =>
    useStoreMutation({
      mutationFn: (data: IFeature) => createFeature(data),
      invalidateQueries: [featureKeys.lists()],
    });

  // Update feature mutation
  const useUpdateFeature = () =>
    useStoreMutation({
      mutationFn: ({ id, data }: { id: string; data: IFeature }) =>
        updateFeature(id, data),
      invalidateQueries: [featureKeys.lists()],
    });

  // Delete feature mutation
  const useDeleteFeature = () =>
    useStoreMutation({
      mutationFn: (id: string) => deleteFeature(id),
      invalidateQueries: [featureKeys.lists()],
    });

  // Deactivate feature mutation
  const useDeactivateFeature = () => {
    const queryClient = useQueryClientStore((state) => state.queryClient);

    return useStoreMutation({
      mutationFn: async (id: string) => {
        // Guard: a feature cannot be deactivated while it is assigned to a
        // subscription that is itself attached to a tenant.
        const subscriptions = await queryClient.fetchQuery({
          queryKey: subscriptionKeys.byFeature(id),
          queryFn: () => getSubscriptionsByFeature(id),
        });

        if (subscriptions.length > 0) {
          const blocking = await Promise.all(
            subscriptions.map(async (subscription: ISubscription) => {
              const tenants = await queryClient.fetchQuery({
                queryKey: tenantKeys.bySubscription(subscription.id as string),
                queryFn: () =>
                  getTenantsBySubscription(subscription.id as string),
              });
              return { subscription, tenants };
            }),
          );

          const inUse = blocking.filter((b) => b.tenants.length > 0);
          if (inUse.length > 0) {
            const subscriptionTitles = inUse
              .map((b) => b.subscription.title)
              .filter(Boolean)
              .join(', ');
            const error = new Error(
              `${EErrorType.FEATURE_IN_USE}${
                subscriptionTitles ? ` (${subscriptionTitles})` : ''
              }`,
            );
            error.name = EErrorType.FEATURE_IN_USE;
            throw error;
          }
        }

        return deactivateFeature(id);
      },
      invalidateQueries: [featureKeys.lists()],
    });
  };

  // Toggle subscription status mutation
  const useToggleActive = () =>
    useStoreMutation({
      mutationFn: (data: IFeature) => toggleFeatureStatus(data),
      invalidateQueries: [featureKeys.lists()],
    });

  // Set feature as public mutation
  const useTogglePublic = () => {
    const queryClient = useQueryClientStore((state) => state.queryClient);
    return useStoreMutation({
      mutationFn: (feature: IFeature) => togglePublic(feature),
      invalidateQueries: [featureKeys.lists()],
      onSuccess: async (_, feature) => {
        // Invalidate the specific feature detail query after success
        if (feature.id) {
          await queryClient.invalidateQueries({
            queryKey: featureKeys.detail(feature.id as string),
          });
        }
      },
    });
  };

  return {
    useAllFeatures,
    useUserFeatures,
    useActiveFeatures,
    useUserFeature,
    useCreateFeature,
    useUpdateFeature,
    useDeleteFeature,
    useDeactivateFeature,
    useTogglePublic,
    useToggleActive,
  };
};
